import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePreviaPisCofins } from "../PreviaPisCofinsContext";
import TabelaBaseLegal from "../../../components/PisCofins/BaseLegal/TabelaBaseLegal";
import Loading from "../../../components/Loading";
import SnackBar from "../../../components/SnackBar";
import { buscarBaseLegalPorVersao, marcarResumoVersaoPrevia, buscarPreviaVersaoPisCofins } from "../../../services/api-piscofins.service";
import { buscarPlanTribBaseLegal } from "../../../services/api-planejamento-tributario.service";
import WhiteButton from "../../../components/WhiteButton";
import DownloadIcon from "@mui/icons-material/Download";
import { formatValue } from "../../../utils/formatValue";
import { exportarBaseLegalPDF } from "../../../utils/exportarBaseLegalPDF";
import StepsIndicator from "../../../components/PisCofins/StepsIndicator";
import styles from "./styles.module.css";
import {
  usePermissoes,
  CHAVES_PIS_COFINS_EDIT,
  CHAVES_PIS_COFINS_EXPORT,
} from "../../../config/Context/PermissoesContext";

const BaseLegal = () => {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const { versaoId, script } = useParams();
  const { dadosVersao, versaoSelecionada, isProjetoIniciado, setPreviaInfo } = usePreviaPisCofins();
  
  const [loading, setLoading] = useState(false);
  const [dadosAgrupados, setDadosAgrupados] = useState(null);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [basesLegais, setBasesLegais] = useState([]);
  const [loadingBasesLegais, setLoadingBasesLegais] = useState(false);

  // Usar dadosVersao do contexto ou parâmetros da URL
  const versaoIdFinal = Number(versaoId) || dadosVersao?.versaoId;
  const scriptFinal = script || dadosVersao?.script || "CONCILIACAO_SPED";
  const totais = dadosAgrupados?.totais;
  
  // Verificar se a prévia está com status PROJETO-INICIADO (validação centralizada)
  const isBloqueada = isProjetoIniciado() || !pode(CHAVES_PIS_COFINS_EDIT);

  const formatCurrency = (valor = 0) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);

  const formatarCnpj = (cnpj) => {
    if (!cnpj) return "Não informado";
    const cnpjLimpo = cnpj.replace(/\D/g, "");
    if (cnpjLimpo.length === 14) {
      return cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return cnpj;
  };

  const carregarDadosAgrupados = async () => {
    if (!versaoIdFinal) {
      console.warn('⚠️ [BaseLegal] Dados incompletos para carregar:', { versaoIdFinal });
      return;
    }

    setLoading(true);
    try {
      const resultado = await buscarBaseLegalPorVersao(versaoIdFinal);

      if (resultado?.sucesso) {
        // Garantir formato esperado (linhas array, totais objeto) para compatibilidade com API
        setDadosAgrupados({
          ...resultado,
          linhas: Array.isArray(resultado.linhas) ? resultado.linhas : [],
          totais: resultado.totais && typeof resultado.totais === "object" ? resultado.totais : {},
        });
      } else {
        setObjSnackBar({
          type: "error",
          msg: resultado?.mensagem || "Erro ao carregar dados agrupados por base legal.",
        });
      }
    } catch (error) {
      console.error('❌ [BaseLegal] Erro ao carregar dados agrupados:', error);
      setObjSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          "Erro ao carregar dados do motor.",
      });
    } finally {
      setLoading(false);
    }
  };

  const carregarBasesLegais = useCallback(async () => {
    setLoadingBasesLegais(true);
    try {
      const resultado = await buscarPlanTribBaseLegal();
      const itens = Array.isArray(resultado?.itens)
        ? resultado.itens
        : Array.isArray(resultado)
        ? resultado
        : [];
      setBasesLegais(itens);
    } catch (error) {
      console.error("Erro ao carregar bases legais:", error);
      setBasesLegais([]);
    } finally {
      setLoadingBasesLegais(false);
    }
  }, []);

  useEffect(() => {
    if (!versaoIdFinal) {
      console.error('❌ [BaseLegal] ID da versão não encontrado');
      setObjSnackBar({
        type: "error",
        msg: "ID da versão não encontrado. Redirecionando...",
      });
      setTimeout(() => {
        navigate("/piscofins/previas");
      }, 2000);
      return;
    }

    // Carregar dados diretamente
    carregarDadosAgrupados();
  }, [versaoIdFinal, scriptFinal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregarBasesLegais();
  }, [carregarBasesLegais]);

  // Buscar e atualizar dados da prévia no contexto
  useEffect(() => {
    const buscarDadosPrevia = async () => {
      if (!versaoIdFinal) return;
      
      try {
        const response = await buscarPreviaVersaoPisCofins(versaoIdFinal);
        if (response?.previa) {
          setPreviaInfo(prev => ({
            ...prev,
            ...response.previa
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar dados da prévia:', error);
      }
    };
    
    buscarDadosPrevia();
  }, [versaoIdFinal, setPreviaInfo]);

  const empresaNome =
    dadosAgrupados?.empresa ||
    dadosVersao?.nomeEmpresa ||
    dadosVersao?.empresa ||
    dadosVersao?.razaoSocial ||
    "Empresa não informada";
  const cnpjEmpresa = formatarCnpj(
    dadosAgrupados?.cnpjEmpresa ||
      dadosVersao?.cnpjMatriz ||
      dadosVersao?.cnpj ||
      ""
  );
  
  // Extrair período para concatenar as datas
  const periodo = dadosAgrupados?.periodo || dadosVersao?.periodo || null;
  const dtInicial = periodo?.dtInicial || periodo?.inicio || dadosVersao?.dtInicial || null;
  const dtFinal = periodo?.dtFinal || periodo?.fim || dadosVersao?.dtFinal || null;
  const dtInicialFormatada = formatValue("data", dtInicial) || "";
  const dtFinalFormatada = formatValue("data", dtFinal) || "";
  const periodoFormatado = dtInicialFormatada && dtFinalFormatada 
    ? `${dtInicialFormatada} a ${dtFinalFormatada}`
    : dtInicialFormatada || dtFinalFormatada || "-";
  
  const temasSelecionados = Array.isArray(dadosVersao?.temasSelecionados)
    ? dadosVersao.temasSelecionados.join(" • ")
    : dadosVersao?.temaSelecionado || "Sob Despesas";

  // Função para exportar dados
  const handleExportar = async () => {
    if (!dadosAgrupados || !dadosAgrupados.linhas || dadosAgrupados.linhas.length === 0) {
      setObjSnackBar({
        type: "error",
        msg: "Nenhum dado disponível para exportar.",
      });
      return;
    }

    setLoading(true);
    try {
      // Preparar informações do cabeçalho
      const infoCabecalho = {
        empresaNome,
        cnpjEmpresa: dadosAgrupados?.cnpjEmpresa || dadosVersao?.cnpjMatriz || dadosVersao?.cnpj || "",
        dtInicial: dtInicialFormatada,
        dtFinal: dtFinalFormatada,
        temasSelecionados,
      };

      // Exportar para PDF
      const resultadoExportacao = await exportarBaseLegalPDF({
        linhas: dadosAgrupados.linhas || [],
        basesLegais,
        infoCabecalho,
        totais: dadosAgrupados.totais || {},
      });

      if (resultadoExportacao?.sucesso) {
        setObjSnackBar({
          type: "success",
          msg: `Arquivo ${resultadoExportacao.nomeArquivo} exportado com sucesso! ${dadosAgrupados.linhas.length} registro(s) exportado(s).`,
        });
      }
    } catch (error) {
      console.error("Erro ao exportar para PDF:", error);
      setObjSnackBar({
        type: "error",
        msg: error?.message || "Erro ao exportar arquivo PDF. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !dadosAgrupados) {
    return <Loading loading={true} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span>Empresa</span>
            <strong>{empresaNome}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>CNPJ</span>
            <strong>{cnpjEmpresa}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Período</span>
            <strong>{periodoFormatado}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Temas selecionados</span>
            <strong>{temasSelecionados}</strong>
          </div>
        </div>

        <StepsIndicator activeStep={2} /> {/* Ajustado de 3 para 2 - etapa BaseCredito removida */}

        {totais && (
          <div className={styles.totalsRow}>
            <div className={`${styles.totalCard} ${styles.totalEcd}`}>
              <span>Total ECD</span>
              <strong>{formatCurrency(totais.valorTotalEcd)}</strong>
            </div>
            <div className={`${styles.totalCard} ${styles.totalPis}`}>
              <span>Total PIS (1,65%)</span>
              <strong>{formatCurrency(totais.pis)}</strong>
            </div>
            <div className={`${styles.totalCard} ${styles.totalCofins}`}>
              <span>Total COFINS (7,6%)</span>
              <strong>{formatCurrency(totais.cofins)}</strong>
            </div>
          </div>
        )}

        {dadosAgrupados && (
          <>
            {pode(CHAVES_PIS_COFINS_EXPORT) && (
            <div className={styles.filtrosContainer}>
              <div className={styles.filtrosRight}>
                <button
                  type="button"
                  onClick={handleExportar}
                  className={styles.botaoExportar}
                >
                  <DownloadIcon style={{ fontSize: "1rem", marginRight: "0.5rem" }} />
                  <span>Exportar</span>
                </button>
              </div>
            </div>
            )}

            <TabelaBaseLegal
              linhas={dadosAgrupados.linhas || []}
              basesLegais={basesLegais}
              loading={loading || loadingBasesLegais}
              isBloqueada={isBloqueada}
            />

            <div className={styles.footer}>
              <div className={styles.footerLeft}>
                <WhiteButton
                  textValue="Voltar"
                  // Botão Voltar aqui deve apenas retornar para a tela 1 (Sob Despesas)
                  clickFunction={() =>
                    navigate(`/piscofins/previas/${versaoIdFinal}/sob-despesas`)
                  }
                />
              </div>

              <div className={styles.footerRight}>
                {pode(CHAVES_PIS_COFINS_EDIT) && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!versaoIdFinal) {
                      setObjSnackBar({
                        type: "error",
                        msg: "ID da versão não encontrado. Não foi possível salvar.",
                      });
                      return;
                    }

                    // Guard clause para versão bloqueada
                    if (isBloqueada) {
                      setObjSnackBar({
                        type: "warning",
                        msg: "Não é possível salvar. Esta versão está em análise ou aprovada.",
                      });
                      return;
                    }

                    setLoading(true);
                    try {
                      // Extrair valores dos totais calculados
                      const valores = {
                        script: scriptFinal || "CONCILIACAO_SPED",
                        pis: totais?.pis || 0,
                        cofins: totais?.cofins || 0,
                        total: (totais?.pis || 0) + (totais?.cofins || 0),
                      };

                      const resultado = await marcarResumoVersaoPrevia(versaoIdFinal, valores);
                      if (resultado?.sucesso) {
                        setObjSnackBar({
                          type: "success",
                          msg: resultado?.mensagem || "Resumo salvo com sucesso!",
                        });
                        // Navegar após um pequeno delay para mostrar a mensagem
                        setTimeout(() => {
                          navigate("/piscofins/previas");
                        }, 1000);
                      } else {
                        setObjSnackBar({
                          type: "error",
                          msg: resultado?.mensagem || "Erro ao salvar resumo.",
                        });
                      }
                    } catch (error) {
                      console.error("Erro ao salvar resumo:", error);
                      setObjSnackBar({
                        type: "error",
                        msg: error?.response?.data?.mensagem || "Erro ao salvar resumo. Por favor, tente novamente.",
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || isBloqueada}
                  className={styles.botaoSalvar}
                >
                  <span>Salvar</span>
                </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </div>
  );
};

export default BaseLegal;

